import { useApolloClient } from '@apollo/client'
import { useCreateCart } from '@emico-hooks/cart-create'
import { useCartId, useCartIdContext } from '@emico-hooks/cart-id'
import { mergeCarts } from '@emico-hooks/cart-merge'
import { generateCustomerToken } from '@emico-hooks/generate-customer-token'
import {
  useLoginTokenContext,
  getCustomerTokenHeaderObject,
} from '@emico-hooks/login-token'
import { useCallback } from 'react'

interface UseLoginOptions {
  useCartHooks?: boolean
}

export function useLogin({ useCartHooks = true }: UseLoginOptions = {}) {
  const apolloClient = useApolloClient()
  const cartId = useCartId()
  const cartIdVar = useCartIdContext()
  const createCart = useCreateCart()
  const loginTokenVar = useLoginTokenContext()

  return useCallback(
    async (email: string, password: string) => {
      const { data: customerToken } = await generateCustomerToken(
        apolloClient,
        email,
        password,
      )

      const token = customerToken?.generateCustomerToken?.token

      if (!token) {
        throw new Error('Received no token')
      }

      if (!useCartHooks) {
        loginTokenVar(token)

        return {
          // Return the complete result so it is possible to request more fields by replacing the
          // @emico-hooks/generate-customer-token package.
          result: customerToken,
          cartId: undefined,
        }
      }

      // Create a temporary token context so we do not need to write the
      // loginToken to state yet. This avoids a render flash and possible
      // errors.
      const temporaryTokenContext = getCustomerTokenHeaderObject(token)

      const newCartId = await createCart(false, {
        context: temporaryTokenContext,
      })

      if (cartId) {
        // If the user already had a cart, he had a guest cart. Guest carts can
        // not be used by logged in users. When logging in we want to preserve the
        // cart, so we need to create a logged-in-cart, and merge the guest cart
        // into it.
        try {
          await mergeCarts(apolloClient, token, cartId, newCartId)
        } catch {
          // "Some products are out of stock" is a likely response, in which case
          // the cart will still be partially merged. Even if M2 has a fatal error
          // while merging, this should not block the login attempt.
          // TODO: Show the user that some products were removed from their cart
        }
      }

      loginTokenVar(token)
      cartIdVar(newCartId)

      return {
        result: customerToken,
        cartId: newCartId,
      }
    },
    [apolloClient, cartId, cartIdVar, createCart, loginTokenVar, useCartHooks],
  )
}
